import { useRef, useEffect, useCallback } from 'react';

/** Match sidebar slide so recenter eases with the chrome. */
const RECENTER_MS = 280;

/**
 * useResizeHandler Hook
 *
 * Handles container resize events:
 * 1. Instantly pins content to the same screen position (no jump when the
 *    sidebar steals/returns space on the left)
 * 2. Eases into a half-delta recenter so the graph settles in the new viewport
 *
 * IMPORTANT: The ResizeObserver effect must NOT depend on `panHook` identity —
 * that object is new every render and was cancelling the recenter mid-flight
 * (felt like a hard left/right snap).
 */
export function useResizeHandler({
  containerRef,
  containerRectRef,
  panHook,
}) {
  const prevContainerRectRef = useRef({ width: 0, height: 0, left: 0, top: 0 });
  const skipResizeAdjustmentUntilRef = useRef(0);
  const animRef = useRef(null);
  const panHookRef = useRef(panHook);
  panHookRef.current = panHook;

  const skipResizeAdjustment = useCallback((durationMs = 500) => {
    skipResizeAdjustmentUntilRef.current = Date.now() + durationMs;
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const initialRect = el.getBoundingClientRect();
    const initial = {
      width: initialRect.width,
      height: initialRect.height,
      left: initialRect.left,
      top: initialRect.top,
    };
    containerRectRef.current = initial;
    prevContainerRectRef.current = initial;

    const cancelAnim = () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
    };

    const writeTransform = (pan) => {
      const canvas = containerRef.current?.querySelector('.flowchart__canvas');
      if (!canvas) return;
      const hook = panHookRef.current;
      const zoom = hook.zoomRef?.current ?? 1;
      canvas.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
      canvas.style.setProperty('--inverse-zoom', 1 / zoom);
      canvas.dispatchEvent(
        new CustomEvent('panupdate', {
          detail: { x: pan.x, y: pan.y, zoom },
        }),
      );
    };

    const easeOutCubic = (t) => 1 - (1 - t) ** 3;

    const animateTo = (start, target) => {
      cancelAnim();

      const dx = target.x - start.x;
      const dy = target.y - start.y;
      const hook = panHookRef.current;

      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        hook.panRef.current = target;
        hook.setPan(target);
        return;
      }

      // Drive the first frame immediately so motion starts this paint,
      // then continue on rAF for the ease.
      const t0 = performance.now();

      const tick = (now) => {
        const t = Math.min(1, (now - t0) / RECENTER_MS);
        const e = easeOutCubic(t);
        const next = {
          x: start.x + dx * e,
          y: start.y + dy * e,
        };

        const h = panHookRef.current;
        h.panRef.current = next;
        writeTransform(next);

        if (t < 1) {
          animRef.current = requestAnimationFrame(tick);
        } else {
          animRef.current = null;
          h.setPan(target, true);
        }
      };

      // Kick immediately (don't wait a frame) so recenter isn't a delayed snap
      tick(t0);
    };

    const resizeObserver = new ResizeObserver(() => {
      const node = containerRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const next = {
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top,
      };
      const prev = prevContainerRectRef.current;

      containerRectRef.current = next;

      if (prev.width === 0 && prev.height === 0) {
        prevContainerRectRef.current = next;
        return;
      }

      if (Date.now() < skipResizeAdjustmentUntilRef.current) {
        prevContainerRectRef.current = next;
        return;
      }

      const deltaW = next.width - prev.width;
      const deltaH = next.height - prev.height;
      const shiftX = prev.left - next.left;
      const shiftY = prev.top - next.top;

      prevContainerRectRef.current = next;

      if (deltaW === 0 && deltaH === 0 && shiftX === 0 && shiftY === 0) {
        return;
      }

      const hook = panHookRef.current;
      const current = hook.panRef.current;

      // Pin to screen (invisible when only the left edge moved), then ease
      // to the half-delta recenter — that's the smooth slide you see.
      const pinned = {
        x: current.x + shiftX,
        y: current.y + shiftY,
      };
      const target = {
        x: current.x + deltaW / 2,
        y: current.y + deltaH / 2,
      };

      hook.panRef.current = pinned;
      writeTransform(pinned);
      animateTo(pinned, target);
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      cancelAnim();
    };
    // Intentionally omit panHook — use panHookRef so re-renders don't kill the ease
  }, [containerRef, containerRectRef]);

  return {
    skipResizeAdjustment,
  };
}

export default useResizeHandler;
